import {
  BrandTilesSlice,
  BrandedStoreSliceType,
  LearnStripSlice,
  MainHeroSlice,
  PromoTilesSlice,
} from '@adapters/prismic/types/brandedStorePage';
import mapBrandTilesData from '@adapters/prismic/utils/mapBrandTilesData';
import mapLearnStripData from '@adapters/prismic/utils/mapLearnStripData';
import mapMainHeroData from '@adapters/prismic/utils/mapMainHeroData';
import mapPromoTilesData from '@adapters/prismic/utils/mapPromoTilesData';
import {
  ActivityNavigationGridSlice,
  transformActivityNavigationGrid,
} from '@adapters/prismic/transforms/activityNavigationGrid';
import {
  ImageHotspotSlice,
  transformImageHotspot,
} from '@adapters/prismic/transforms/imageHotspot';
import type { AlgonomyPlacement } from '@components/global/Recommendations/Loader';
import {
  NOT_SET,
  ViewComponentLocation,
  ViewComponentType,
} from '_utils/GA4/trackViewComponent';
import { isSliceRendered } from './sliceVisibility';

type SliceLike = {
  slice_type?: string;
  primary?: any;
  items?: any;
} | null;

export interface ViewTracking {
  location: ViewComponentLocation;
  type: ViewComponentType;
  heading: string;
  index: number;
}

const normalizeHeading = (text?: string | null): string =>
  text?.replace(/\s+/g, ' ').trim() || NOT_SET;

const toTracked = (type: ViewComponentType, heading?: string | null) => ({
  type,
  heading: normalizeHeading(heading),
});

const findPlacement = (
  slice: SliceLike,
  recommendations?: AlgonomyPlacement[] | null
): AlgonomyPlacement | undefined =>
  recommendations?.find(({ name }) => name === slice?.primary?.placement_name);

const isEmptyPlacement = (
  slice: SliceLike,
  recommendations?: AlgonomyPlacement[] | null
): boolean =>
  slice?.slice_type === 'algonomy_placement' &&
  !findPlacement(slice, recommendations)?.items?.length;

export const describeTrackedSlice = (
  slice: SliceLike,
  recommendations?: AlgonomyPlacement[] | null
): { type: ViewComponentType; heading: string } | null => {
  switch (slice?.slice_type) {
    case BrandedStoreSliceType.mainHero:
      return toTracked(
        'hero',
        mapMainHeroData((slice.primary ?? {}) as MainHeroSlice['primary']).h1
      );
    case BrandedStoreSliceType.activityNavigationGrid:
      return toTracked(
        'activity nav grid',
        transformActivityNavigationGrid(
          slice as unknown as ActivityNavigationGridSlice
        )?.heading
      );
    case BrandedStoreSliceType.promoTiles: {
      const { primary, items } = slice as PromoTilesSlice;
      return toTracked(
        'promo tiles',
        mapPromoTilesData(primary, items).heading
      );
    }
    case BrandedStoreSliceType.learnStrip: {
      const { primary, items } = slice as LearnStripSlice;
      return toTracked(
        'learn strip',
        mapLearnStripData(primary, items).heading
      );
    }
    case 'image_hotspot':
      return toTracked(
        'hotspot',
        transformImageHotspot(slice as unknown as ImageHotspotSlice)?.heading
      );
    case BrandedStoreSliceType.brandTiles: {
      const { primary, items } = slice as BrandTilesSlice;
      return toTracked(
        'brand tiles',
        mapBrandTilesData(primary, items).heading
      );
    }
    case 'algonomy_placement': {
      const placement = findPlacement(slice, recommendations);
      return placement?.items?.length
        ? toTracked('recommendation', placement.message)
        : null;
    }
    default:
      return null;
  }
};

export const buildViewTracking = (
  slices: SliceLike[] | undefined,
  location: ViewComponentLocation,
  recommendations?: AlgonomyPlacement[] | null,
  resolved?: Parameters<typeof isSliceRendered>[1]
): (ViewTracking | null)[] => {
  let position = 0;

  return (slices ?? []).map((slice) => {
    if (
      !isSliceRendered(slice, resolved) ||
      isEmptyPlacement(slice, recommendations)
    ) {
      return null;
    }

    const index = position;
    position += 1;

    const tracked = describeTrackedSlice(slice, recommendations);
    return tracked ? { location, ...tracked, index } : null;
  });
};
